export interface Workshop {
    id: string
    title: string
    description: string
    durationDays: number
    price: number
    ageMin: number
    startDate: string | null
    endDate: string | null
    maxParticipants: number | null
    itchIoUrl: string | null
    weezEventUrl: string | null
    imageUrl: string | null
    imageAlt: string | null
    iconName: string | null
    currentParticipants: number
}

export interface WorkshopRegisterRequest {
    firstName: string
    lastName: string
    email: string
    phone?: string
    website?: string
    formLoadedAt?: number
}

export interface WorkshopRegisterResponse {
    checkoutUrl: string
    resumeToken: string
}

export interface WorkshopResumeData {
    id: string
    firstName: string
    lastName: string
    email: string
    phone: string | null
    workshop: {
        id: string
        title: string
        price: number
        durationDays: number
    }
}

export const useWorkshops = () => {
    const config = useRuntimeConfig()
    const apiBase = config.public.apiBase

    const getWorkshops = async (): Promise<Workshop[]> => {
        const { data, error } = await useFetch<Workshop[]>(`${apiBase}/oxoweb/workshops`)
        if (error.value) {
            console.error('Error fetching workshops:', error.value)
            return []
        }
        return data.value ?? []
    }

    const register = async (
        workshopId: string,
        payload: WorkshopRegisterRequest
    ): Promise<WorkshopRegisterResponse> => {
        const data = await $fetch<WorkshopRegisterResponse>(
            `${apiBase}/oxoweb/workshops/${workshopId}/register`,
            { method: 'POST', body: payload }
        )
        return data
    }

    const getResumeData = async (token: string): Promise<WorkshopResumeData | null> => {
        try {
            const data = await $fetch<WorkshopResumeData>(
                `${apiBase}/oxoweb/workshops/registrations/resume?token=${encodeURIComponent(token)}`
            )
            return data
        } catch {
            return null
        }
    }

    const resumePayment = async (token: string): Promise<string | null> => {
        try {
            const data = await $fetch<{ checkoutUrl: string }>(
                `${apiBase}/oxoweb/workshops/registrations/resume`,
                { method: 'POST', body: { token } }
            )
            return data.checkoutUrl
        } catch {
            return null
        }
    }

    const isFull = (workshop: Workshop): boolean =>
        workshop.maxParticipants !== null &&
        workshop.currentParticipants >= workshop.maxParticipants

    return {
        getWorkshops,
        register,
        getResumeData,
        resumePayment,
        isFull
    }
}
